import React from "react";
import { COLLECTIONS, SIZES, priceBounds } from "../../data/products";
import { products } from "../../data/products";
import { formatPrice, cn } from "../../lib/utils";

const uniqueColors = () => {
  const map = new Map();
  products.forEach((p) => p.colors.forEach((c) => map.set(c.name, c)));
  return Array.from(map.values());
};

const Group = ({ title, children }) => (
  <div className="border-b border-ink/10 py-6">
    <h4 className="label-mono mb-4">{title}</h4>
    {children}
  </div>
);

const FilterPanel = ({ filters, setFilters }) => {
  const { min, max } = priceBounds();
  const colors = uniqueColors();

  const toggle = (key, value) => {
    setFilters((prev) => {
      const list = prev[key];
      return {
        ...prev,
        [key]: list.includes(value)
          ? list.filter((v) => v !== value)
          : [...list, value],
      };
    });
  };

  return (
    <div data-testid="filter-panel">
      <Group title="Collection">
        <div className="space-y-2.5">
          {COLLECTIONS.map((c) => (
            <label
              key={c.slug}
              className="flex cursor-pointer items-center gap-3 text-sm"
              data-testid={`filter-collection-${c.slug}`}
            >
              <input
                type="checkbox"
                checked={filters.collections.includes(c.slug)}
                onChange={() => toggle("collections", c.slug)}
                className="h-4 w-4 accent-ink"
              />
              {c.name}
            </label>
          ))}
        </div>
      </Group>

      <Group title="Size">
        <div className="flex flex-wrap gap-2">
          {SIZES.map((s) => {
            const active = filters.sizes.includes(s);
            return (
              <button
                key={s}
                onClick={() => toggle("sizes", s)}
                data-testid={`filter-size-${s}`}
                className={cn(
                  "flex h-10 min-w-[2.75rem] items-center justify-center border px-2 font-mono text-xs uppercase transition-colors",
                  active ? "border-ink bg-ink text-paper" : "border-ink/25 hover:border-ink"
                )}
              >
                {s}
              </button>
            );
          })}
        </div>
      </Group>

      <Group title="Color">
        <div className="flex flex-wrap gap-2.5">
          {colors.map((c) => {
            const active = filters.colors.includes(c.name);
            return (
              <button
                key={c.name}
                onClick={() => toggle("colors", c.name)}
                title={c.name}
                aria-label={c.name}
                aria-pressed={active}
                data-testid={`filter-color-${c.name.toLowerCase()}`}
                className={cn(
                  "h-8 w-8 rounded-full transition-transform hover:scale-105",
                  active ? "ring-2 ring-ink ring-offset-2" : "ring-1 ring-ink/20"
                )}
                style={{ backgroundColor: c.hex }}
              />
            );
          })}
        </div>
      </Group>

      <Group title={`Max Price · ${formatPrice(filters.maxPrice)}`}>
        <input
          type="range"
          min={min}
          max={max}
          step={50}
          value={filters.maxPrice}
          onChange={(e) => setFilters((p) => ({ ...p, maxPrice: Number(e.target.value) }))}
          className="w-full accent-ink"
          data-testid="filter-price-range"
        />
        <div className="mt-2 flex justify-between font-mono text-xs text-ink/50">
          <span>{formatPrice(min)}</span>
          <span>{formatPrice(max)}</span>
        </div>
      </Group>
    </div>
  );
};

export default FilterPanel;
